import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { STARTKAPITTEL, sporsmaalssti, veiledningssti } from '../lib/hjelpesider';

/** Hjelpesidene slik de står åpent, utenfor appen. */
const HJELP = veiledningssti(STARTKAPITTEL, 'offentlig');
const SPORSMAL = sporsmaalssti('offentlig');

/**
 * Toppen og bunnen på de offentlige sidene.
 *
 * Sto tidligere inni forsiden, som var den eneste sida som hadde dem. Da
 * prislista fikk sin egen adresse, ble det tydelig hvorfor de ikke kunne bli
 * der: en delt lenke lander på en side uten merke, uten meny og uten vei
 * videre, og ser ut som noe som har falt ut av nettstedet framfor å være en del
 * av det.
 */

/** Hvor «kom i gang»-knappen skal føre, gitt hvem som ser på. */
export function useHovedvei(): { href: string; tekst: string; innlogget: boolean } {
  const { status, weddings } = useAuth();
  const innlogget = status === 'authenticated';
  return {
    innlogget,
    href: innlogget ? (weddings.length > 0 ? '/app' : '/kom-i-gang') : '/registrer',
    tekst: innlogget ? 'Gå til planleggingen' : 'Kom i gang gratis',
  };
}

/**
 * @param snarveier Lenker som bare gir mening på sida de står på – forsidens
 *   ankere til «slik virker det» og «verktøy» finnes ikke på prislista, og en
 *   meny som peker på ingenting er verre enn en kort meny.
 */
export function Nettstedstopp({
  snarveier = [],
}: {
  snarveier?: { href: string; tekst: string }[];
}) {
  const { href, innlogget } = useHovedvei();
  const [rullet, setRullet] = useState(false);

  useEffect(() => {
    const paaRulling = () => setRullet(window.scrollY > 8);
    paaRulling();
    window.addEventListener('scroll', paaRulling, { passive: true });
    return () => window.removeEventListener('scroll', paaRulling);
  }, []);

  return (
    <header className={`site-header${rullet ? ' scrolled' : ''}`}>
      <div className="container inner">
        <Link className="brand" to="/">
          <span className="brand-mark" aria-hidden="true">
            VD
          </span>
          Vår Dag
        </Link>
        <nav className="site-nav">
          {snarveier.map((snarvei) => (
            <a className="hide-sm" href={snarvei.href} key={snarvei.href}>
              {snarvei.tekst}
            </a>
          ))}
          {/* Prislista er en side og ikke et anker, og skal derfor lenkes til
              med <Link> også fra forsiden: et anker hit ville lastet siden på
              nytt for å komme til en adresse appen allerede har. */}
          <Link className="hide-sm" to="/priser">
            Priser
          </Link>
          {/* Veiledningen, ikke spørsmålene: den som ikke kjenner tjenesten,
              vil vite hva den gjør, og det er det første kapittelet som
              forteller. */}
          <Link className="hide-sm" to={HJELP}>
            Hjelp
          </Link>
          {innlogget ? (
            <Link className="btn btn-primary btn-sm" to={href}>
              Til planleggingen
            </Link>
          ) : (
            <>
              <Link to="/logg-inn">Logg inn</Link>
              <Link className="btn btn-primary btn-sm" to="/registrer">
                Kom i gang
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export function Nettstedsbunn() {
  return (
    <footer className="site-footer">
      <div className="container inner">
        <div>
          <strong>Vår Dag</strong> · Bryllupsplanlegging gjort enkelt
        </div>
        <nav className="footer-links">
          <Link to="/priser">Priser</Link>
          <Link to={HJELP}>Brukerveiledning</Link>
          <Link to={SPORSMAL}>Vanlige spørsmål</Link>
          <Link to="/gjesteinformasjon">Til deg som er gjest</Link>
          <Link to="/vilkaar">Bruksvilkår</Link>
          <Link to="/salgsbetingelser">Salgsbetingelser</Link>
          <Link to="/personvern">Personvern</Link>
          <Link to="/logg-inn">Logg inn</Link>
        </nav>
      </div>
    </footer>
  );
}
